/**
 * Computes whether a business is currently open from its weekly schedule.
 *
 * Schedule is stored as seven `string[]` fields (one per day), each holding
 * time ranges in 24h "HH:mm-HH:mm" form, e.g. ["09:00-18:00", "20:00-23:00"].
 * Overnight ranges (end <= start, e.g. "20:00-02:00") are supported and spill
 * into the following day.
 *
 * Returns null when the business has no schedule data at all, so the UI can
 * simply omit the indicator rather than showing a misleading "Closed".
 */

export interface BusinessSchedule {
  schedule_sun?: string[] | null;
  schedule_mon?: string[] | null;
  schedule_tue?: string[] | null;
  schedule_wed?: string[] | null;
  schedule_thu?: string[] | null;
  schedule_fri?: string[] | null;
  schedule_sat?: string[] | null;
}

export interface BusinessOpenState {
  state: 'open' | 'closed';
  /** Lowercase verb + time, e.g. "closes 11:00 PM" or "opens 9:00 AM". */
  label: string;
}

// Indexed by Date.getDay() (0 = Sunday).
const DAY_FIELDS: Array<keyof BusinessSchedule> = [
  'schedule_sun',
  'schedule_mon',
  'schedule_tue',
  'schedule_wed',
  'schedule_thu',
  'schedule_fri',
  'schedule_sat',
];

function toMinutes(value: string): number | null {
  const m = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

function formatTime(minutesOfDay: number): string {
  const mins = ((minutesOfDay % 1440) + 1440) % 1440;
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

interface Range {
  start: number;
  /** May exceed 1440 when the range crosses midnight. */
  end: number;
}

function dayRanges(business: BusinessSchedule, dayIndex: number): Range[] {
  const raw = business[DAY_FIELDS[dayIndex]];
  if (!Array.isArray(raw)) return [];
  const ranges: Range[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const [a, b] = entry.split('-');
    const start = toMinutes(a ?? '');
    let end = toMinutes(b ?? '');
    if (start == null || end == null) continue;
    if (end <= start) end += 1440; // overnight
    ranges.push({ start, end });
  }
  return ranges;
}

export function getBusinessOpenState(
  business: BusinessSchedule,
  now: Date = new Date(),
): BusinessOpenState | null {
  const hasSchedule = DAY_FIELDS.some((_, i) => dayRanges(business, i).length > 0);
  if (!hasSchedule) return null;

  const dow = now.getDay();
  const cur = now.getHours() * 60 + now.getMinutes();

  // Currently inside one of today's ranges?
  for (const r of dayRanges(business, dow)) {
    if (cur >= r.start && cur < r.end) {
      return { state: 'open', label: `closes ${formatTime(r.end)}` };
    }
  }

  // Inside an overnight range that started yesterday and spills into today?
  const prevDay = (dow + 6) % 7;
  for (const r of dayRanges(business, prevDay)) {
    if (r.end > 1440 && cur < r.end - 1440) {
      return { state: 'open', label: `closes ${formatTime(r.end - 1440)}` };
    }
  }

  // Closed — find the next opening within the coming week.
  for (let d = 0; d < 7; d++) {
    const idx = (dow + d) % 7;
    const ranges = dayRanges(business, idx).sort((a, b) => a.start - b.start);
    for (const r of ranges) {
      if (d === 0 && r.start <= cur) continue; // already past today
      return { state: 'closed', label: `opens ${formatTime(r.start)}` };
    }
  }

  return { state: 'closed', label: 'closed' };
}
